// LOBBY PLAYGROUND (§6.1): the toy-box lid, the READY pad, three vote-card pads, the vending machine
// and two punching dummies. Map logic:
//  - READY pad: a grounded bean standing on it fills `readyTicks` (replicated, so the fill ring is
//    predicted); 90 continuous ticks emit READY_PAD (b = 1), which the room turns into Ready.
//    Leaving the pad empties the ring (the room keeps the Ready flag; only the UI toggle clears it).
//  - Vote pads: `votePadOf(w, slot)` tells the room which card a bean stands on (−1 for none).
//  - Vending machine: a bean with an empty item slot in the trigger circle gets a random item from
//    the round's pool (server RNG), then a 240-tick personal cooldown (`cdVend`).
// Dummies are generic props (sim/props.ts), homed at `DUMMY_POSTS`.
import { NO_SLOT, TUNE, VOID } from '../constants';
import { EV, emit } from '../events';
import { BS, EK, ItemKind, MapId } from '../ids';
import { randInt } from '../rng';
import { isGrounded } from '../sim/edges';
import { spawnEntity } from '../sim/entities';
import { giveItem } from '../sim/items';
import { isPredicting, isPuppet } from '../sim/world';
import type { Bean, Vec2, World } from '../types';
import { MAP_TEMPO } from './clock';
import { boxSolids, copySolids, nearSolid, neutralHazardPose } from './common';
import { inRoundedRect, nearestOnRoundedRect } from './geometry';
import { LID } from './lid';
import { SolidTag, type MapDef } from './mapTypes';

export const TUNING = {
  readyPad: { x: 0, y: 3.2, radius: 1.4 },
  /** Vote-card pads 3.4 × 2.4 m, cards 1–3 left to right. */
  votePads: { width: 3.4, depth: 2.4, centers: [{ x: -5, y: -2.8 }, { x: 0, y: -2.8 }, { x: 5, y: -2.8 }] },
  vending: { x: 0, y: -5.4, width: 2.0, depth: 0.8, top: 2.0, edgeRadius: 0.05, triggerX: 0, triggerY: -4.5, triggerRadius: 1.0 },
  dummyPosts: [{ x: -6, y: 2.5 }, { x: 6, y: 2.5 }],
  /** Two rows in front of the ready pad, clear of the vote-card pads and dummies. */
  spawns: [
    { x: -6, y: 0.2 }, { x: -3.6, y: 0.2 }, { x: -1.2, y: 0.2 }, { x: 1.2, y: 0.2 },
    { x: 3.6, y: 0.2 }, { x: 6, y: 0.2 }, { x: -2.4, y: 1.9 }, { x: 2.4, y: 1.9 },
  ],
} as const;

export { LID };

export const DUMMY_POSTS: readonly Vec2[] = TUNING.dummyPosts;

const V = TUNING.vending;
const PAD = TUNING.readyPad;
const SOLIDS = boxSolids(V.x, V.y, V.width, V.depth, V.edgeRadius, V.top, TUNE.body.restitution, SolidTag.NONE);

/** A bean whose feet are on the lid (not hanging, falling, carried, floating or parachuting). */
function standing(b: Bean): boolean {
  if (!b.active || b.out) return false;
  switch (b.state) {
    case BS.FALLING: case BS.GHOST: case BS.RESPAWN: case BS.GRABBED: case BS.FLOAT: case BS.LEDGE: case BS.CLIMB: case BS.FROZEN:
      return false;
    default:
      return isGrounded(b);
  }
}

/** True if (x, y) is on the READY pad. */
export function onReadyPad(x: number, y: number): boolean {
  return (x - PAD.x) ** 2 + (y - PAD.y) ** 2 <= PAD.radius * PAD.radius;
}

/** Index (0–2) of the vote-card pad under (x, y), or −1. */
export function votePadAt(x: number, y: number): number {
  const P = TUNING.votePads;
  for (let i = 0; i < P.centers.length; i++) {
    const c = P.centers[i];
    if (Math.abs(x - c.x) <= P.width / 2 && Math.abs(y - c.y) <= P.depth / 2) return i;
  }
  return -1;
}

/** The vote-card pad bean `slot` stands on (0–2), or −1 (§6.1: standing on a card counts as a vote). */
export function votePadOf(w: World, slot: number): number {
  const b = w.beans[slot];
  return b && standing(b) ? votePadAt(b.pos.x, b.pos.y) : -1;
}

function stepReadyPad(w: World): void {
  const server = !isPredicting(w);
  for (const b of w.beans) {
    if (!b.active || isPuppet(w, b.slot)) continue;
    if (!standing(b) || !onReadyPad(b.pos.x, b.pos.y)) {
      b.readyTicks = 0;
      continue;
    }
    if (b.readyTicks >= TUNE.lobby.readyTicks) continue;
    if (++b.readyTicks === TUNE.lobby.readyTicks && server) emit(w, EV.READY_PAD, b.slot, 1, 0, 0, PAD.x, PAD.y);
  }
}

function stepVending(w: World): void {
  const pool = w.rules.itemPool;
  if (isPredicting(w) || pool.length === 0) return;
  const r2 = V.triggerRadius * V.triggerRadius;
  for (const b of w.beans) {
    if (!standing(b) || b.item !== ItemKind.NONE || b.cdVend > 0) continue;
    if ((b.pos.x - V.triggerX) ** 2 + (b.pos.y - V.triggerY) ** 2 > r2) continue;
    const kind = pool[randInt(w, pool.length)];
    b.cdVend = TUNE.lobby.vendCooldownTicks;
    emit(w, EV.VEND, b.slot, kind, 0, 0, V.triggerX, V.triggerY);
    giveItem(w, b.slot, kind);
  }
}

export const LOBBY: MapDef = {
  id: MapId.LOBBY,
  name: 'Lobby Playground',
  tpb: MAP_TEMPO[MapId.LOBBY].tpb,
  tpbSD: MAP_TEMPO[MapId.LOBBY].tpbSD,
  bounds: { minX: LID.cx - LID.hx, maxX: LID.cx + LID.hx, minY: LID.cy - LID.hy, maxY: LID.cy + LID.hy },
  spawns: TUNING.spawns.map((p) => ({ x: p.x, y: p.y })),
  voidKind: 'lilac',
  center: { x: 0, y: 0 },
  initState(w) {
    DUMMY_POSTS.forEach((p, i) => {
      spawnEntity(w, EK.DUMMY, NO_SLOT, p.x, p.y, 0).data = i;
    });
  },
  groundAt(_w, x, y) {
    return inRoundedRect(x, y, LID) ? 0 : VOID;
  },
  nearestGround(_w, x, y, out) {
    return nearestOnRoundedRect(x, y, LID, out);
  },
  solids(_w, out) {
    return copySolids(SOLIDS, out);
  },
  onSolidContact() {},
  step(w) {
    stepReadyPad(w);
    stepVending(w);
  },
  danger() {
    return 0;
  },
  hazardPose(_w, _tick, out) {
    return neutralHazardPose(out);
  },
  crateExclusion(_w, x, y) {
    return nearSolid(SOLIDS, x, y, TUNE.crate.radius);
  },
};
